export interface ChipSelectOption {
  value: string;
  text: string;
  html?: string;
  key?: string;
  /** Shown above the label in the cards layout and beside it in the list layout. */
  imageUrl?: string;
  /** A short emoji or symbol shown before the label. */
  icon?: string;
  /** With multiple selections, picking this clears the others (e.g. "None of the above"). */
  exclusive?: boolean;
}

/**
 * - `chips`: compact inline pills (the original look).
 * - `list`: full-width stacked answer buttons, one per row.
 * - `cards`: a responsive grid of tiles, typically with an image each.
 */
export type ChipSelectLayout = 'chips' | 'list' | 'cards';

/** Plain HTML input types, plus 'textarea' for a multi-line custom value. */
export type ChipSelectCustomValueType =
  | 'text'
  | 'number'
  | 'url'
  | 'email'
  | 'tel'
  | 'date'
  | 'datetime-local'
  | 'time'
  | 'textarea';

export interface ChipSelectData {
  field_name?: string;
  required?: boolean;
  options?: ChipSelectOption[];
  multiple?: boolean;
  layout?: ChipSelectLayout;
  /** Move to the next step as soon as an option is picked (single select only). */
  autoAdvance?: boolean;
  allowCustomValue?: boolean;
  customValuePlaceholder?: string;
  /** How the custom-value field is entered; plain text when unset. */
  customValueType?: ChipSelectCustomValueType;
  value?: string | string[];
}

export const CHIP_SELECT_LAYOUTS: { value: ChipSelectLayout; label: string }[] = [
  { value: 'chips', label: 'Chips (compact)' },
  { value: 'list', label: 'List (full-width buttons)' },
  { value: 'cards', label: 'Cards (image grid)' },
];

export const layoutShowsImages = (layout?: ChipSelectLayout): boolean =>
  layout === 'list' || layout === 'cards';

/** Auto-advance only makes sense when one click fully answers the question. */
export const canAutoAdvance = (data: ChipSelectData): boolean =>
  !data.multiple && !data.allowCustomValue;
